import { useState, useEffect, useMemo } from 'react';
import styles from '../../styles/UserComparisonChart.module.css';
import {
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  TextField,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label
} from 'recharts';
import logger from '../../../../utils/logger';
import AuditServices from '../../../../services/audit.services';
import { useDashboardSettings } from '../../context/DashboardSettingsContext';

// Color palette for user lines/bars
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8',
  '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'
];

// Helper function to sanitize email address to display format
const sanitizeUsername = (username) => {
  if (!username) return 'Unknown';

  try {
    // If it's an email, extract only the part before @ and replace dots with spaces
    if (username.includes('@')) {
      const [name] = username.split('@');
      // Convert name.surname to Name Surname (capitalized)
      return name.split('.')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }

    return username;
  } catch (err) {
    logger.error('Error sanitizing username:', err);
    return username || 'Unknown';
  }
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        padding: '10px',
        borderRadius: '5px',
        boxShadow: '0px 2px 5px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: '0', fontWeight: 'bold' }}>{`Date: ${label || 'Unknown'}`}</p>
        {payload.map((entry, index) => {
          try {
            // Extract raw username from dataKey
            const rawUsername = entry.dataKey;
            const displayName = sanitizeUsername(rawUsername);

            return (
              <p key={`item-${index}`} style={{ margin: '0', color: entry.color }}>
                {`${displayName}: ${entry.value || 0}`}
              </p>
            );
          } catch (err) {
            logger.error('Error rendering tooltip item:', err);
            return null;
          }
        })}
      </div>
    );
  }

  return null;
};

// Custom Legend component that handles interactivity
const CustomizedLegend = (props) => {
  const { payload, activeUser, onUserClick } = props;

  if (!payload || !Array.isArray(payload)) {
    return null;
  }

  return (
    <ul style={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      listStyle: 'none',
      padding: 0,
      margin: '10px 0 0'
    }}>
      {payload.map((entry, index) => {
        if (!entry || !entry.value) return null;

        try {
          const rawUsername = entry.value;
          const displayName = sanitizeUsername(rawUsername);
          const isActive = activeUser === entry.value;

          return (
            <li
              key={`legend-item-${index}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginRight: 20,
                marginBottom: 10,
                cursor: 'pointer',
                color: '#000000',
                transition: 'all 0.2s ease',
                backgroundColor: isActive ? '#f0f0f0' : 'transparent',
                padding: '3px 8px',
                borderRadius: '4px',
                opacity: activeUser && !isActive ? 0.5 : 1
              }}
              onClick={() => onUserClick(entry.value)}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  marginRight: 5,
                  backgroundColor: entry.color,
                  borderRadius: '50%'
                }}
              />
              {displayName}
            </li>
          );
        } catch (err) {
          logger.error('Error rendering legend item:', err);
          return null;
        }
      })}
    </ul>
  );
};

/**
 * User Comparison Chart component that works independently from settings panel
 * All selection controls (users, modules, chart type) are available directly on the chart
 */
const UserComparisonChart = ({
  startDate,
  endDate,
  initialUsers = []
}) => {
  const [selectedUsers, setSelectedUsers] = useState(initialUsers || []);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableModules, setAvailableModules] = useState([
    'all', 'BOUNCES', 'COMPANIES', 'COMPANY', 'JIRA', 'LOGIN',
    'MEDIARELATIONS', 'OBJECTEXPLORER', 'SCRIPTRUNNER', 'SEARCHES', 'SSE'
  ]); // Initialize with common modules
  const [currentChartType, setCurrentChartType] = useState('bar');
  const [currentTimeView, setCurrentTimeView] = useState('weekly');
  const [currentModules, setCurrentModules] = useState(['all']);
  const [topUsersLoaded, setTopUsersLoaded] = useState(false);

  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeUser, setActiveUser] = useState(null);

  // Fetch available users and modules
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        // Fetch all users with activity data
        const response = await AuditServices.getUserStats(
          1, 1000,
          startDate,
          endDate,
          'totalCount',
          'desc'
        );

        if (response && response.users) {
          // Set available users from response
          const usernames = response.users.map(user => user.username).filter(username => username);
          setAvailableUsers(usernames);

          // Extract unique modules from user data
          const uniqueModules = new Set(['all']);
          response.users.forEach(user => {
            if (user.moduleStats) {
              Object.keys(user.moduleStats).forEach(module => {
                if (module && module.trim() !== '') {
                  uniqueModules.add(module);
                }
              });
            }
          });

          // Convert to array and sort
          const sortedModules = Array.from(uniqueModules).sort();
          logger.info('Available modules:', sortedModules);
          setAvailableModules(sortedModules);
        } else {
          setAvailableUsers([]);
        }

        setError(null);
      } catch (err) {
        logger.error('Error fetching users and modules:', err);
        setError('Failed to load users and modules. Please try again.');
        setAvailableUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [startDate, endDate]);

  // Initialize with initialUsers when component mounts or they change
  useEffect(() => {
    if (initialUsers && Array.isArray(initialUsers) && initialUsers.length > 0 &&
      (!selectedUsers.length || selectedUsers.length === 0) && !topUsersLoaded) {
      // Filter out any null/undefined values
      const validUsers = initialUsers.filter(user => user);
      if (validUsers.length > 0) {
        setSelectedUsers(validUsers);
        setTopUsersLoaded(true);
      }
    }
  }, [initialUsers, selectedUsers, topUsersLoaded]);

  // Get the active module for data fetching - use first selected module or 'all'
  const getActiveModule = useMemo(() => {
    if (!currentModules || currentModules.length === 0 || currentModules.includes('all')) {
      return 'all';
    }
    return currentModules[0] || 'all';
  }, [currentModules]);

  // Fetch time-series data for selected users
  useEffect(() => {
    const fetchTimeSeriesData = async () => {
      if (selectedUsers.length === 0) {
        setTimeSeriesData([]);
        return;
      }

      setLoading(true);
      try {
        logger.info('Fetching time series data for users:', selectedUsers);
        logger.info('With active module:', getActiveModule);

        const data = await AuditServices.getUserTimeSeriesData(
          selectedUsers,
          startDate,
          endDate,
          getActiveModule
        );

        logger.info('Received time series data:', data);

        // Process the data to ensure it's in the right format
        const processedData = Array.isArray(data) ? data : [];

        // Convert modules from array to object for easier data processing
        const processedDataWithModulesObject = processedData.map(item => {
          if (item.modules && Array.isArray(item.modules)) {
            const modulesObj = {};
            item.modules.forEach(mod => {
              if (mod && mod.module) {
                modulesObj[mod.module] = mod.count;
              }
            });
            return { ...item, modules: modulesObj };
          }
          return item;
        });

        setTimeSeriesData(processedDataWithModulesObject);
        setError(null);
      } catch (err) {
        logger.error('Error fetching user time-series data', err);
        setError('Failed to load user activity data. Please try again.');
        setTimeSeriesData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeSeriesData();
  }, [selectedUsers, getActiveModule, startDate, endDate]);

  // Handle user selection changes
  const handleUserChange = (event, newValue) => {
    // Limit to max 10 users
    if (newValue.length <= 10) {
      setSelectedUsers(newValue);
    }
  };

  // Handle multiple module selection changes
  const handleModulesChange = (event) => {
    const value = event.target.value;
    logger.info('Module selection changed to:', value);

    // Handle "All Modules" special case - it can't be combined with other modules
    if (value.includes('all')) {
      if (currentModules.includes('all') && value.length === 1) {
        // If "all" was already selected and user clicked it again, do nothing
        return;
      } else {
        // If "all" was newly selected, make it the only selection
        logger.info('Setting modules to [all]');
        setCurrentModules(['all']);
      }
    } else {
      // For regular modules, allow multiple selection (except "all")
      logger.info('Setting modules to', value);
      setCurrentModules(value);
    }
  };

  // Handle time granularity changes
  const handleGranularityChange = (event) => {
    const newTimeView = event.target.value;
    setCurrentTimeView(newTimeView);
  };

  // Handle chart type changes
  const handleChartTypeChange = (event, newChartType) => {
    if (newChartType !== null) {
      setCurrentChartType(newChartType);
    }
  };

  // Handle user click in legend
  const handleUserClick = (user) => {
    // If clicking the same user, toggle off
    if (activeUser === user) {
      setActiveUser(null);
    } else {
      setActiveUser(user);
    }
  };

  // Generate chart data based on the time-series data and selected options
  const chartData = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0 || !selectedUsers || selectedUsers.length === 0) {
      return [];
    }

    logger.info('Generating chart data with:', {
      timeSeriesData: timeSeriesData.length,
      selectedUsers,
      currentModules,
      currentTimeView
    });

    // Helper function to format periods for display (moved inside useMemo)
    const formatPeriodForDisplay = (period) => {
      if (period.includes('W')) {
        const [year, week] = period.split('-W');
        return `W${week} ${year}`;
      } else if (period.includes('M')) {
        const [year, month] = period.split('-M');
        return `M${month} ${year}`;
      } else {
        // Daily view - format as MM/DD
        const date = new Date(period);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    };

    // Filter data by time granularity
    const filteredData = timeSeriesData.filter(item => {
      if (!item) return false;
      if (currentTimeView === 'daily') return item.type === 'daily';
      if (currentTimeView === 'weekly') return item.type === 'weekly';
      if (currentTimeView === 'monthly') return item.type === 'monthly';
      return false;
    });

    logger.info('Filtered data by time view:', filteredData.length);

    // Group data by period
    const dataByPeriod = new Map();

    filteredData.forEach(item => {
      if (!item) return;

      const { period, username } = item;
      if (!period || !username) return;

      // Initialize period data if not exist
      if (!dataByPeriod.has(period)) {
        dataByPeriod.set(period, {
          period,
          formattedPeriod: formatPeriodForDisplay(period)
        });
      }

      const periodData = dataByPeriod.get(period);

      // Store user's value for this period
      // Use total count or specific module count based on selected module
      if (!currentModules || currentModules.length === 0 || currentModules.includes('all')) {
        // Use total count for all modules
        periodData[username] = item.totalCount || 0;
      } else {
        // Sum up counts for selected modules
        let moduleSum = 0;
        currentModules.forEach(moduleName => {
          if (item.modules && moduleName && item.modules[moduleName]) {
            moduleSum += item.modules[moduleName];
          }
        });
        periodData[username] = moduleSum;
      }
    });

    // Ensure all users have entries for all periods (fill with 0 if missing)
    dataByPeriod.forEach(dataPoint => {
      selectedUsers.forEach(username => {
        if (username && dataPoint[username] === undefined) {
          dataPoint[username] = 0;
        }
      });
    });

    // Convert map to array and sort by period
    let sortedData = Array.from(dataByPeriod.values());
    sortedData = sortedData.sort((a, b) => {
      const periodA = a.period;
      const periodB = b.period;

      if (!periodA || !periodB) return 0;

      if (periodA.includes('W') && periodB.includes('W')) {
        return periodA.localeCompare(periodB);
      } else if (periodA.includes('M') && periodB.includes('M')) {
        return periodA.localeCompare(periodB);
      } else {
        try {
          // For daily view, use date comparison
          return new Date(periodA) - new Date(periodB);
        } catch (err) {
          logger.error('Error comparing dates:', err);
          return 0;
        }
      }
    });

    logger.info('Generated chart data:', sortedData);
    return sortedData;
  }, [timeSeriesData, selectedUsers, currentModules, currentTimeView]);

  // Automatically load top 5 users if no users are selected
  useEffect(() => {
    if (!topUsersLoaded && availableUsers.length > 0 && selectedUsers.length === 0) {
      logger.info('Auto-loading top users from available users:', availableUsers.slice(0, 5));
      const topUsers = availableUsers.slice(0, 5);
      setSelectedUsers(topUsers);
      setTopUsersLoaded(true);
    } else if (initialUsers && initialUsers.length > 0 && selectedUsers.length === 0 && !topUsersLoaded) {
      logger.info('Using initial users:', initialUsers);
      setSelectedUsers(initialUsers);
      setTopUsersLoaded(true);
    }
  }, [topUsersLoaded, availableUsers, selectedUsers, initialUsers]);

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3, height: '100%', minHeight: 500, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6">User Activity Comparison</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: { xs: 1, sm: 0 } }}>
          <ToggleButtonGroup
            value={currentChartType}
            exclusive
            onChange={handleChartTypeChange}
            size="small"
            aria-label="chart type"
            sx={{
              '& .Mui-selected': {
                color: '#1D9999 !important',
                backgroundColor: 'rgba(40, 187, 187, 0.08) !important'
              }
            }}
          >
            <ToggleButton value="line" className={currentChartType === 'line' ? styles['selected-button'] : ''}>Line</ToggleButton>
            <ToggleButton value="bar" className={currentChartType === 'bar' ? styles['selected-button'] : ''}>Bar</ToggleButton>
          </ToggleButtonGroup>

          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel
              id="time-granularity-label"
              sx={{ '&.Mui-focused': { color: '#1D9999' } }}
            >
              Time View
            </InputLabel>
            <Select
              labelId="time-granularity-label"
              id="time-granularity-select"
              value={currentTimeView}
              label="Time View"
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1D9999',
                }
              }}
              onChange={handleGranularityChange}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel
              id="module-select-label"
              sx={{ '&.Mui-focused': { color: '#1D9999' } }}
            >
              Module
            </InputLabel>
            <Select
              labelId="module-select-label"
              id="module-select"
              multiple
              value={currentModules || ['all']}
              label="Module"
              onChange={handleModulesChange}
              renderValue={(selected) => {
                if (selected.includes('all')) return 'All Modules';
                return selected.join(', ');
              }}
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1D9999',
                }
              }}
            >
              {availableModules && availableModules.length > 0 ? (
                availableModules.map((module) => (
                  <MenuItem key={module} value={module}>
                    <Checkbox checked={currentModules.includes(module)} />
                    <ListItemText primary={module === 'all' ? 'All Modules' : module} />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No modules available</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Autocomplete
          multiple
          id="user-selection"
          options={availableUsers}
          value={selectedUsers}
          onChange={handleUserChange}
          getOptionLabel={(option) => option || ''}
          isOptionEqualToValue={(option, value) => option === value}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Select Users (max 10)"
              placeholder={selectedUsers.length === 0 ? "Select up to 10 users to compare" : ""}
              fullWidth
              size="small"
            />
          )}
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => (
              <Chip
                key={option}
                label={sanitizeUsername(option)}
                {...getTagProps({ index })}
                size="small"
                style={{
                  backgroundColor: COLORS[index % COLORS.length],
                  color: '#fff',
                  fontWeight: 600,
                  textShadow: '0px 1px 2px rgba(0,0,0,0.5)',
                  boxShadow: 'inset 0px 1px 3px rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              />
            ))
          }
          size="small"
          disableCloseOnSelect
          limitTags={5}
        />
      </Box>

      <Box sx={{ height: 400, position: 'relative' }}>
        {loading && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 10
          }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && selectedUsers.length === 0 && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
            color: 'text.secondary'
          }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Select up to 10 users to compare their activity over time
            </Typography>
            <Typography variant="body2">
              Each user's activity will be shown with a different color
            </Typography>
          </Box>
        )}

        {!loading && !error && selectedUsers.length > 0 && chartData.length === 0 && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'text.secondary'
          }}>
            <Typography>No data available for selected criteria</Typography>
          </Box>
        )}

        {!loading && !error && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            {currentChartType === 'line' ? (
              <LineChart
                data={chartData}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 40,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="formattedPeriod"
                  height={60}
                  angle={-45}
                  textAnchor="end"
                  tick={{ fontSize: 12 }}
                />
                <YAxis>
                  <Label
                    value={currentModules.includes('all') ? "Total Activity" : `${currentModules.join(', ')} Activity`}
                    angle={-90}
                    position="insideLeft"
                    style={{ textAnchor: 'middle' }}
                  />
                </YAxis>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  content={props => (
                    <CustomizedLegend
                      {...props}
                      activeUser={activeUser}
                      onUserClick={handleUserClick}
                    />
                  )}
                />

                {selectedUsers.map((user, index) => {
                  const isActive = activeUser === user;
                  const color = COLORS[index % COLORS.length];

                  return (
                    <Line
                      key={user}
                      type="monotone"
                      dataKey={user}
                      name={user}
                      stroke={color}
                      strokeWidth={isActive ? 3 : 2}
                      activeDot={{ r: 8 }}
                      dot={{ strokeWidth: 2 }}
                      opacity={activeUser && !isActive ? 0.3 : 1}
                    />
                  );
                })}
              </LineChart>
            ) : (
              <BarChart
                data={chartData}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 40,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="formattedPeriod"
                  height={60}
                  angle={-45}
                  textAnchor="end"
                  tick={{ fontSize: 12 }}
                />
                <YAxis>
                  <Label
                    value={currentModules.includes('all') ? "Total Activity" : `${currentModules.join(', ')} Activity`}
                    angle={-90}
                    position="insideLeft"
                    style={{ textAnchor: 'middle' }}
                  />
                </YAxis>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  content={props => (
                    <CustomizedLegend
                      {...props}
                      activeUser={activeUser}
                      onUserClick={handleUserClick}
                    />
                  )}
                />

                {selectedUsers.map((user, index) => {
                  const isActive = activeUser === user;
                  const color = COLORS[index % COLORS.length];

                  return (
                    <Bar
                      key={user}
                      dataKey={user}
                      name={user}
                      fill={color}
                      opacity={activeUser && !isActive ? 0.3 : 1}
                    />
                  );
                })}
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </Box>

      {!loading && !error && selectedUsers.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
          Showing {currentModules.includes('all') ? 'total activity across all modules' : `activity for ${currentModules.join(', ')} module(s)`} by {currentTimeView} view
        </Typography>
      )}
    </Paper>
  );
};

export default UserComparisonChart;

/**
 * Enhanced User Comparison Chart that works directly with the global date range
 * It no longer depends on the settings side panel for functionality
 */
const EnhancedUserComparisonChart = () => {
  const { globalSettings } = useDashboardSettings();
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get global date range from context (from DateRangeSelector)
  const { startDate, endDate } = globalSettings;

  // Load top users on initial render or when date range changes
  useEffect(() => {
    const fetchTopUsers = async () => {
      try {
        setLoading(true);
        // Fetch top users for the "all" module
        const response = await AuditServices.getUserStats(
          1, 1000,
          startDate,
          endDate,
          'totalCount',
          'desc'
        );

        if (response && response.users && response.users.length > 0) {
          // Take top 5 users
          const topFiveUsers = response.users
            .slice(0, 5)
            .map(user => user.username)
            .filter(username => username); // Filter out any null/undefined values

          logger.info('Got top users from API:', topFiveUsers);
          setTopUsers(topFiveUsers);
        } else {
          logger.info('No users returned from API');
          setTopUsers([]);
        }
        setError(null);
      } catch (err) {
        logger.error('Error fetching top users', err);
        setError('Failed to load top users data. Please try again.');
        setTopUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopUsers();
  }, [startDate, endDate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <UserComparisonChart
      startDate={startDate}
      endDate={endDate}
      initialUsers={topUsers}
    />
  );
};

export { EnhancedUserComparisonChart };